import { useState, type ReactNode } from 'react';
import { resolveLiveBusy, resolveLiveStart, runResolveLive, t, type ResolveLiveState, type ResolveOutcome } from '@bms2/shared';
import { Modal } from './Modal';
import styles from './modal.module.css';

/**
 * The resolve-live refusal modal (piece 5, decision 0442).
 *
 * 🔨 A refusal for a missing prerequisite opens this, not an error banner, when
 * the person in front of the screen is allowed to supply the missing thing. It
 * names what is missing, takes it, writes it, and re-runs the act that was
 * refused. The product owner's words on the tax register, 2026-09-16: *"resolve
 * this live, don't throw an error, throw another modal that states the issue and
 * gathers what is needed to resolve it."*
 *
 * 🔨 The banner stays for every refusal this cannot cure — no permission, or a
 * rule the plan forbids crossing. Offering a form for something the server will
 * refuse again teaches a person to fill in a form that never works.
 *
 * 🔨 This opens over the modal that was refused rather than replacing it, so
 * the original act still has its own draft to run against when the cure lands.
 * The stacking rule of decision 0138 already holds the layer beneath inert.
 *
 * The sequencing is `runResolveLive` in `packages/shared`, with no DOM in it
 * (decision 0242): the cure is written first and the act re-run second, and a
 * cure already written is never written twice by a second attempt.
 */
export const resolveLiveModalDocumentedStates = ['gathering', 'refused', 'writing'] as const;

export interface ResolveLiveModalProps {
  /** The refusal the person just hit, shown at the top of the Body. */
  refusal: string;
  title: string;
  subtitle?: string;
  /** The label on the Primary: what saving the cure and re-running will do. */
  confirmLabel: string;
  /** What the form asks for, above the Footer. */
  children: ReactNode;
  /** Writes the missing prerequisite. */
  onResolve: () => Promise<ResolveOutcome>;
  /** Re-runs the act that was refused, once the prerequisite is there. */
  onRetry: () => Promise<ResolveOutcome>;
  /** Both done: the host closes this and the original modal together. */
  onSettled: () => void;
  onCancel: () => void;
  /** The Primary is dead until the form holds what the cure needs. */
  disabled?: boolean;
  dataModal?: string;
}

export function ResolveLiveModal({
  refusal, title, subtitle, confirmLabel, children, onResolve, onRetry, onSettled, onCancel, disabled = false, dataModal = 'resolve-live',
}: ResolveLiveModalProps) {
  const [state, setState] = useState<ResolveLiveState>(() => resolveLiveStart(refusal));
  const busy = resolveLiveBusy(state);

  const act = async () => {
    const end = await runResolveLive(state, onResolve, onRetry, setState);
    if (end.step === 'settled') onSettled();
  };

  const shellProps = { ...(subtitle === undefined ? {} : { subtitle }) };
  return (
    <Modal
      open
      title={title}
      {...shellProps}
      onCancel={onCancel}
      dataModal={dataModal}
      actions={[
        { kind: 'primary', label: confirmLabel, onAct: () => void act(), disabled: disabled || busy },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {/* The refusal is stated rather than summarised: the person has to be able
          to tell that this modal is about the thing they just tried to do. */}
      <div className={styles.refusal} data-resolve-live-refusal="true" role="status">
        <span className={styles.refusalHeading}>{t('resolveLive.heading')}</span>
        <span className={styles.refusalText}>{state.refusal}</span>
      </div>
      <p className={styles.refusalHint}>{t('resolveLive.hint')}</p>
      {children}
      {busy ? (
        <p className={styles.refusalHint} data-resolve-live-step={state.step}>
          {state.step === 'writing' ? t('resolveLive.writing') : t('resolveLive.retrying')}
        </p>
      ) : null}
    </Modal>
  );
}
